import { useState, useEffect } from 'react'
import * as Dialog from '@radix-ui/react-dialog'

interface Props {
  open: boolean
  onOpenChange: (v: boolean) => void
  /** Called with an ISO 8601 string when the user confirms. */
  onConfirm: (scheduledFor: string) => void
  isPending: boolean
  pageTitle?: string
  /** Pre-seed the input with the current scheduled time if known. */
  currentScheduledFor?: string | null
}

export function ReschedulePageDialog({
  open, onOpenChange, onConfirm, isPending, pageTitle, currentScheduledFor,
}: Props) {
  const [value, setValue] = useState('')

  // Seed / reset whenever the dialog opens or target page changes.
  useEffect(() => {
    if (open && currentScheduledFor) {
      setValue(new Date(currentScheduledFor).toISOString().slice(0, 16))
    } else if (open) {
      setValue('')
    }
  }, [open, currentScheduledFor])

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-96 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-canvas p-5 shadow-xl focus:outline-none"
          aria-describedby="reschedule-page-dialog-desc"
        >
          <Dialog.Title className="text-sm font-semibold text-fg">
            Change publication schedule
          </Dialog.Title>
          <Dialog.Description id="reschedule-page-dialog-desc" className="mt-1 text-xs text-fg-secondary">
            {pageTitle}
          </Dialog.Description>
          <div className="mt-4">
            <label
              className="block text-[11px] font-semibold text-fg-secondary"
              htmlFor="reschedule-page-datetime"
            >
              New publication date &amp; time
            </label>
            <input
              id="reschedule-page-datetime"
              type="datetime-local"
              className="mt-1.5 w-full rounded border border-line bg-surface px-3 py-2 text-[12px] text-fg focus:border-line-strong focus:outline-none"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={() => { if (value) onConfirm(new Date(value).toISOString()) }}
              disabled={!value || isPending}
              className="rounded bg-teal-9 px-3 py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-40"
            >
              {isPending ? 'Saving…' : 'Update schedule'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
